import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown, ExternalLink, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError } from "../../lib/api";
import { mapTokenResponse } from "../../lib/auth-tokens";
import { formatPhoneInput, isValidLocalPhone, normalizePhone } from "../../lib/phone";
import { getDeviceId, getAdminDeviceToken, setAdminDeviceToken, getDeviceName } from "../../lib/device";
import { AuthFooterLink, AuthLink } from "../../layouts/AuthLayout";

import { OtpInput } from "../../components/ui/OtpInput";
import { StatusBanner } from "../../components/ui/StatusBanner";
import { getAndClearRedirectTarget, isValidRedirectPath, saveRedirectTarget } from "../../lib/redirect";

const botUsername = (import.meta.env.VITE_BOT_USERNAME || "soorgaa_bot").replace(/^@/, "");

type AuthFlow = "login" | "register" | "link_telegram" | "password" | "admin_otp";

/** Phone → Telegram code on your phone → enter code on this device. */
export function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setAuth, isAuthenticated, role } = useAuth();

  useEffect(() => {
    const redirectParam = searchParams.get("redirect");
    if (redirectParam && isValidRedirectPath(redirectParam)) {
      saveRedirectTarget(redirectParam);
    }
  }, [searchParams]);

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [botUrl, setBotUrl] = useState<string | null>(null);
  const [flow, setFlow] = useState<AuthFlow | null>(null);
  const [status, setStatus] = useState<{
    phone_verified: boolean;
    otp_sent: boolean;
    is_registered: boolean | null;
    flow?: AuthFlow;
  } | null>(null);
  const [infoMessage, setInfoMessage] = useState("");

  const [fullName, setFullName] = useState("");
  const [universities, setUniversities] = useState<{ id: string; name: string }[]>([]);
  const [universityId, setUniversityId] = useState("");
  const [streams, setStreams] = useState<{ id: string; name: string }[]>([]);
  const [streamId, setStreamId] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  function handleBack() {
    if (sessionToken) {
      setSessionToken(null);
      setBotUrl(null);
      setFlow(null);
      setStatus(null);
      setPassword("");
      setOtp("");
      setError("");
      setInfoMessage("");
    } else {
      if (location.key !== "default") {
        navigate(-1);
      } else {
        navigate("/", { replace: true });
      }
    }
  }

  useEffect(() => {
    Promise.all([api.universities(), api.streams()])
      .then(([uniList, streamList]) => {
        setUniversities(uniList);
        if (uniList[0]) setUniversityId(uniList[0].id);
        setStreams(streamList);
        if (streamList[0]) setStreamId(streamList[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionToken || flow === "login" || flow === "password" || flow === "admin_otp") return;
    if (status?.phone_verified) return;

    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.pollSession(sessionToken);
        if (cancelled) return;
        setStatus(s);
        if (s.flow) setFlow(s.flow);
        if (s.phone_verified) {
          setInfoMessage(
            s.is_registered
              ? "Phone linked. Enter the code sent to your Telegram."
              : "Phone verified. Complete your profile below.",
          );
        }
      } catch { /* session expired */ }
    };

    tick();
    const id = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sessionToken, flow, status?.phone_verified]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  if (isAuthenticated) {
    const redirectParam = searchParams.get("redirect");
    const target = (redirectParam && isValidRedirectPath(redirectParam))
      ? redirectParam
      : getAndClearRedirectTarget();
    return <Navigate to={target || (role === "admin" ? "/admin" : "/student")} replace />;
  }

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    if (!phone.trim()) {
      setError("Enter your phone number");
      return;
    }
    if (!isValidLocalPhone(phone)) {
      setError("Enter a valid phone number: 9 digits starting with 9 or 7, for example 91 234 5678.");
      return;
    }
    setLoading(true);
    try {
      const normalized = normalizePhone(formatPhoneInput(phone));
      const res = await api.requestCode(normalized);
      setSessionToken(res.session_token);
      setBotUrl(res.bot_url);
      setFlow(res.flow);
      setStatus({
        phone_verified: res.code_sent,
        otp_sent: res.code_sent,
        is_registered: res.is_registered,
        flow: res.flow,
      });
      setInfoMessage(res.message);
      if (res.code_sent) setResendCooldown(60);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send verification code");
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!sessionToken || resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      const res = await api.resendCode(sessionToken);
      setInfoMessage(res.message);
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not resend verification code");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionToken) return;
    if (!fullName.trim()) {
      setError("Please enter your full name");
      return;
    }
    if (!universityId) {
      setError("Please select a university");
      return;
    }
    if (!streamId) {
      setError("Please select your stream (e.g. Natural or Social)");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.register({
        session_token: sessionToken,
        full_name: fullName.trim(),
        university_id: universityId,
        stream_id: streamId,
      });
      setAuth(mapTokenResponse(res));
      const redirectParam = searchParams.get("redirect");
      const target = (redirectParam && isValidRedirectPath(redirectParam))
        ? redirectParam
        : getAndClearRedirectTarget();
      navigate(target || "/student", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleAdminPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionToken || !password) return;
    setLoading(true);
    setError("");
    setInfoMessage("");
    try {
      const deviceId = getDeviceId();
      const deviceToken = getAdminDeviceToken() || undefined;
      const deviceName = getDeviceName();

      const res = await api.adminVerifyPassword({
        session_token: sessionToken,
        password,
        device_id: deviceId,
        device_token: deviceToken,
        device_name: deviceName,
      });

      if (res.flow === "authenticated" && res.access_token) {
        if (res.device_token) {
          setAdminDeviceToken(res.device_token);
        }
        setAuth(mapTokenResponse(res as any));
        const redirectParam = searchParams.get("redirect");
        const target = (redirectParam && isValidRedirectPath(redirectParam))
          ? redirectParam
          : getAndClearRedirectTarget();
        navigate(target || "/admin");
        return;
      }

      setFlow("admin_otp");
      setStatus({
        phone_verified: true,
        otp_sent: true,
        is_registered: true,
        flow: "admin_otp",
      });
      setInfoMessage(res.message);
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Password verification failed");
    } finally {
      setLoading(false);
    }
  }


  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionToken) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.login(sessionToken, otp.replace(/\D/g, ""));
      setAuth(mapTokenResponse(res));
      const redirectParam = searchParams.get("redirect");
      const target = (redirectParam && isValidRedirectPath(redirectParam))
        ? redirectParam
        : getAndClearRedirectTarget();
      navigate(target || (res.role === "admin" ? "/admin" : "/student"), { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "";
      setError(
        /expired/i.test(msg)
          ? "This code has expired. Tap Resend code to get a new one."
          : err instanceof ApiError && err.status >= 500
            ? "Something went wrong. Please try again."
            : "That code is not correct. Check it and try again.",
      );
      setOtp("");
    } finally {
      setLoading(false);
    }
  }

  const verified = status?.phone_verified;
  const isPassword = flow === "password";
  const isAdminOtp = flow === "admin_otp";
  const isLogin = (flow === "login" && (verified || status?.otp_sent)) || isAdminOtp;
  const isRegister = verified && flow === "register";
  const needsTelegramLink = flow === "link_telegram" || (flow === "register" && !verified);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-xs font-benton font-medium text-muted-foreground hover:text-foreground transition group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          {sessionToken ? "Change phone number" : "Back to home"}
        </button>
        {sessionToken && (
          <span className="text-xs font-mono font-medium text-muted-foreground bg-secondary px-2 py-0.5 rounded-xs border border-border">
            +251 {phone}
          </span>
        )}
      </div>

      <h2 className="font-display text-2xl font-bold text-foreground mb-1">
        Sign in to Soorgaa
      </h2>
      <p className="font-benton text-muted-foreground text-xs sm:text-sm mb-5 leading-relaxed">
        Fast, secure sign-in via Telegram verification.
      </p>

      {error && <div id="auth-error" role="alert"><StatusBanner type="error" message={error} className="mb-4" /></div>}
      {infoMessage && !error && (
        <StatusBanner type="info" message={infoMessage} className="mb-4" />
      )}

      {!sessionToken && (
        <form onSubmit={handleRequestCode} className="space-y-4">
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">Phone number</label>
            <div className="flex gap-2">
              <div className="flex items-center gap-1.5 px-3 py-2 surface-flat text-xs w-20 shrink-0 rounded-xs">
                <span>🇪🇹</span><span>+251</span>
              </div>
              <input
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                onPaste={(e) => {
                  e.preventDefault();
                  setPhone(formatPhoneInput(e.clipboardData.getData("text")));
                }}
                placeholder="91 234 5678"
                autoComplete="tel"
                inputMode="tel"
                aria-describedby={error ? "auth-error" : undefined}
                required
                className="flex-1 px-3 py-2 field text-base sm:text-sm rounded-xs"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full py-2.5 rounded-xs text-xs sm:text-sm disabled:opacity-60 font-semibold"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Continue <ArrowRight size={15} /></>}
          </button>
        </form>
      )}

      {sessionToken && isPassword && (
        <form onSubmit={handleAdminPassword} className="space-y-4 mt-2">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-benton font-semibold">Phone</label>
              <button
                type="button"
                onClick={handleBack}
                className="text-xs font-benton font-semibold text-primary hover:underline"
              >
                Change
              </button>
            </div>
            <input
              value={phone}
              readOnly
              className="w-full px-3 py-2 surface-flat text-xs rounded-xs opacity-75"
            />
          </div>
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">Administrator password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                minLength={6}
                autoComplete="current-password"
                aria-describedby={error ? "auth-error" : undefined}
                className="w-full pl-3 pr-10 py-2.5 field text-base sm:text-sm rounded-xs"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition p-1"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 btn btn-primary text-xs sm:text-sm font-semibold rounded-xs flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Continue <ArrowRight size={15} /></>}
          </button>
        </form>
      )}

      {sessionToken && isLogin && (
        <form onSubmit={handleLogin} className="space-y-4 mt-2">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-benton font-semibold">Phone</label>
              <button
                type="button"
                onClick={handleBack}
                className="text-xs font-benton font-semibold text-primary hover:underline"
              >
                Change
              </button>
            </div>
            <input
              value={phone}
              readOnly
              className="w-full px-3 py-2 surface-flat text-xs rounded-xs opacity-75"
            />
          </div>
          <div>
            <label className="block text-xs font-benton font-semibold mb-2" id="otp-label">6-digit code</label>
            <OtpInput value={otp} onChange={setOtp} onComplete={(_, form) => form?.requestSubmit()} describedBy={error ? "auth-error" : undefined} disabled={loading} autoFocus />
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
              <span>Check your Telegram messages for the code from Soorgaa.</span>
              <a
                href={`https://t.me/${botUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium inline-flex items-center gap-0.5 ml-2 shrink-0"
              >
                Open Telegram bot
              </a>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 btn btn-primary text-xs sm:text-sm font-semibold rounded-xs flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Sign in <ArrowRight size={15} /></>}
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={loading || resendCooldown > 0}
            className="w-full py-1.5 text-xs font-semibold text-primary disabled:text-muted-foreground hover:underline"
          >
            {resendCooldown > 0 ? `Resend code in ${formatCountdown(resendCooldown)}` : "Resend code"}
          </button>
        </form>
      )}

      {sessionToken && needsTelegramLink && botUrl && (
        <div className="space-y-3 p-3.5 rounded-xs border border-border bg-secondary/50 mt-2">
          <p className="text-xs font-semibold text-foreground">
            Link Telegram to continue
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Open the bot once on Telegram to authorize this account.
          </p>
          <a
            href={botUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 btn btn-primary rounded-xs text-xs font-semibold"
          >
            <ExternalLink size={14} /> Open @Soorgaa_bot
          </a>
          <button
            type="button"
            onClick={handleBack}
            className="w-full py-1 text-xs font-benton font-semibold text-muted-foreground hover:text-foreground transition text-center"
          >
            ← Wrong phone number? Go back
          </button>
          <p className="text-xs text-center text-muted-foreground animate-pulse">
            Waiting for confirmation…
          </p>
        </div>
      )}

      {isRegister && (
        <form onSubmit={handleRegister} className="space-y-3.5 mt-3">
          <StatusBanner type="success" message="Phone verified. Complete your profile." />
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-benton font-semibold">Phone</label>
              <button
                type="button"
                onClick={handleBack}
                className="text-xs font-benton font-semibold text-primary hover:underline"
              >
                Change
              </button>
            </div>
            <input
              value={phone}
              readOnly
              className="w-full px-3 py-2 surface-flat text-xs rounded-xs opacity-75"
            />
          </div>
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">Full name *</label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Abebe Bikila"
              autoComplete="name"
              inputMode="text"
              aria-describedby={error ? "auth-error" : undefined}
              required
              className="w-full px-3 py-2 field text-base sm:text-sm rounded-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">Stream *</label>
            <div className="relative">
              <select
                value={streamId}
                onChange={(e) => setStreamId(e.target.value)}
                autoComplete="off"
                required
                className="w-full appearance-none px-3 py-2 field text-base sm:text-sm rounded-xs bg-card"
              >
                {streams.length === 0 && <option value="">Loading streams...</option>}
                {streams.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Choose Natural or Social stream to see your authorized resources and exams.
            </p>
          </div>
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">University</label>
            <div className="relative">
              <select
                value={universityId}
                onChange={(e) => setUniversityId(e.target.value)}
                autoComplete="off"
                required
                className="w-full appearance-none px-3 py-2 field text-base sm:text-sm rounded-xs bg-card"
              >
                {universities.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 btn btn-primary text-xs sm:text-sm font-semibold rounded-xs flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Create account <ArrowRight size={15} /></>}
          </button>
        </form>
      )}

      {sessionToken && flow === "link_telegram" && verified && (
        <form onSubmit={handleLogin} className="space-y-3.5 mt-3">
          <StatusBanner type="success" message="Telegram linked. Enter your verification code." />
          <OtpInput value={otp} onChange={setOtp} onComplete={(_, form) => form?.requestSubmit()} describedBy={error ? "auth-error" : undefined} disabled={loading} autoFocus />
          <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
            <span>Enter the code sent to your Telegram.</span>
            <a
              href={`https://t.me/${botUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium inline-flex items-center gap-0.5 ml-2 shrink-0"
            >
              Open Telegram bot
            </a>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 btn btn-primary text-xs sm:text-sm font-semibold rounded-xs flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Sign in <ArrowRight size={15} /></>}
          </button>
          <button
            type="button"
            onClick={handleBack}
            className="w-full py-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition text-center"
          >
            ← Change phone number
          </button>
        </form>
      )}

      <AuthFooterLink>
        By continuing, you agree to Soorgaa&apos;s study terms.
      </AuthFooterLink>
    </div>
  );
}

export const LoginPage = AuthPage;
export const RegisterPage = AuthPage;
export const VerifyPage = AuthPage;
